import "./MultiColumnCard.scss";

import cx from "classnames";
import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import React, { useEffect, useState } from "react";

type Props<T extends { id: number | string }> = {
  columnNames: (string | React.ReactNode)[];
  RowComponent: React.ComponentType<T>;
  heading: string;
  initialRows: T[];
  onNewRow: () => void;
  onDeleteRow?: (row: T) => Promise<void>;
  classNames: string;
  getRowKey?: (row: T) => string | number;
  isLoading?: boolean;
  leftAlignColumns?: string[];
  layout?: "default" | "inverse";
  showSurgicalPricingButton?: boolean;
  onSurgicalPricingClick?: () => void;
  showRedbookSearchButton?: boolean;
  onRedbookSearchClick?: () => void;
  showAddButton?: boolean;
  id?: string;
};

export const MultiColumnCard = <T extends { id: number | string }>({
  columnNames = [],
  RowComponent,
  heading,
  initialRows = [],
  onNewRow,
  onDeleteRow,
  classNames,
  getRowKey = (row) => row.id,
  isLoading = false,
  leftAlignColumns = [],
  layout = "default",
  showSurgicalPricingButton = false,
  onSurgicalPricingClick,
  showRedbookSearchButton = false,
  onRedbookSearchClick,
  showAddButton = true,
  id,
}: Props<T>) => {
  const [rows, setRows] = useState<T[]>([]);
  const [rowToDelete, setRowToDelete] = useState<T | null>(null);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const addNewRow = () => {
    onNewRow();
  };

  const handleDeleteConfirm = async () => {
    if (rowToDelete && onDeleteRow) {
      await onDeleteRow(rowToDelete);
      setRowToDelete(null);
    }
  };

  return (
    <div
      id={id}
      className={cx("data-block MultiColumnCard", {
        "layout-inverse": layout === "inverse",
      })}
    >
      <div className="data-block-header flex jc-sb">
        {heading}
        <div className="flex jc-end">
          {showRedbookSearchButton && (
            <ButtonIcon
              kind="neutral"
              size="small"
              type="button"
              onClick={onRedbookSearchClick || (() => {})}
              disabled={isLoading}
            >
              search
            </ButtonIcon>
          )}
          {showSurgicalPricingButton && (
            <ButtonIcon
              kind="neutral"
              size="small"
              type="button"
              onClick={onSurgicalPricingClick || (() => {})}
              disabled={isLoading}
            >
              calculate
            </ButtonIcon>
          )}
          {showAddButton && (
            <ButtonIcon
              kind="neutral"
              size="small"
              type="button"
              onClick={addNewRow}
              disabled={isLoading}
            >
              add_circle
            </ButtonIcon>
          )}
        </div>
      </div>

      <div className={cx("data-values", classNames)}>
        {columnNames.map((name, index) => (
          <div
            key={`header-${index}`}
            className={cx("header-primary", {
              "text-left": leftAlignColumns.includes(String(name)),
            })}
          >
            {name}
          </div>
        ))}
        {onDeleteRow && <div className="header-primary"></div>}
      </div>
      {rows.map((row) => (
        <div
          key={`row-${getRowKey(row)}`}
          className={cx("data-values", classNames)}
        >
          <RowComponent {...row} />
          {onDeleteRow && (
            <figure className="data-values-delete flex">
              <ButtonIcon
                size="small"
                type="button"
                className="data-value delete flex as-c"
                onClick={() => setRowToDelete(row)}
                disabled={isLoading}
              >
                delete
              </ButtonIcon>
            </figure>
          )}
        </div>
      ))}

      {rowToDelete && (
        <KaboodleModal
          isOpen={!!rowToDelete}
          onClose={() => setRowToDelete(null)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleDeleteConfirm}>
                Confirm Delete
              </Button>
              <Button onClick={() => setRowToDelete(null)}>Cancel</Button>
            </div>
          }
        >
          <Card heading="Delete Row">
            <p>Are you sure you want to delete this row?</p>
          </Card>
        </KaboodleModal>
      )}
    </div>
  );
};
